/**
 * Admin Dashboard
 * Platform administration interface for super admins and canton admins.
 * Provides user management, system stats, and AI usage analytics.
 *
 * Canton admins see a scoped view of their assigned cantons only.
 * Super admins see the full platform-wide admin dashboard.
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Shield,
  Users,
  Building2,
  Activity,
  Cpu,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Coins,
  Settings,
  MapPin,
  X,
  Loader2,
  Pencil,
  Eye,
  User,
  Mail,
  Calendar,
  Clock,
  Briefcase,
  ExternalLink,
  ShieldCheck,
  Crown,
  Filter,
  Flag,
  Percent,
  Trash2,
  Plus,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { FunnelSection } from "@/components/admin/FunnelSection";

const ADMIN_ROLES = ["admin", "canton_admin", "super_admin"] as const;

// All role values matching backend roleEnumValues
type RoleFilter =
  | "user"
  | "accountant"
  | "admin"
  | "canton_admin"
  | "super_admin"
  | "all";

// Canton status type
type CantonStatus = "active" | "inactive" | "beta";

// Canton assignment permissions type
interface CantonPermissions {
  canManageUsers: boolean;
  canManageCompanies: boolean;
  canManageTaxConfig: boolean;
  canViewAnalytics: boolean;
  canManageCantonAdmins: boolean;
}

// Canton assignment from backend
interface CantonAssignment {
  cantonCode: string;
  isPrimary: boolean;
  permissions: CantonPermissions | null;
  assignedAt: Date | null;
}

// Canton data from API
interface Canton {
  code: string;
  nameDe: string;
  nameEn: string | null;
  capital: string | null;
  status: CantonStatus;
  subdomain: string | null;
  taxConfig: {
    corporateTaxRate?: number;
    capitalTaxRate?: number;
    personalIncomeTaxRate?: number;
    vatExemptions?: string[];
    filingDeadlines?: {
      corporateTax?: string;
      vatMonthly?: string;
      vatQuarterly?: string;
    };
  } | null;
  featureFlags: {
    enableOnboarding?: boolean;
    enableAiAssistant?: boolean;
    enableBankIntegrations?: boolean;
    enableMultiCurrency?: boolean;
    customFeatures?: Record<string, boolean>;
  } | null;
  adminCount: number;
  companyCount: number;
}

// Swiss canton names mapping for display
const CANTON_NAMES: Record<string, { de: string; en: string }> = {
  ZH: { de: "Zurich", en: "Zurich" },
  BE: { de: "Bern", en: "Bern" },
  LU: { de: "Luzern", en: "Lucerne" },
  UR: { de: "Uri", en: "Uri" },
  SZ: { de: "Schwyz", en: "Schwyz" },
  OW: { de: "Obwalden", en: "Obwalden" },
  NW: { de: "Nidwalden", en: "Nidwalden" },
  GL: { de: "Glarus", en: "Glarus" },
  ZG: { de: "Zug", en: "Zug" },
  FR: { de: "Freiburg", en: "Fribourg" },
  SO: { de: "Solothurn", en: "Solothurn" },
  BS: { de: "Basel-Stadt", en: "Basel-City" },
  BL: { de: "Basel-Landschaft", en: "Basel-Country" },
  SH: { de: "Schaffhausen", en: "Schaffhausen" },
  AR: { de: "Appenzell Ausserrhoden", en: "Appenzell Outer Rhodes" },
  AI: { de: "Appenzell Innerrhoden", en: "Appenzell Inner Rhodes" },
  SG: { de: "St. Gallen", en: "St. Gallen" },
  GR: { de: "Graubunden", en: "Grisons" },
  AG: { de: "Aargau", en: "Aargau" },
  TG: { de: "Thurgau", en: "Thurgau" },
  TI: { de: "Tessin", en: "Ticino" },
  VD: { de: "Waadt", en: "Vaud" },
  VS: { de: "Wallis", en: "Valais" },
  NE: { de: "Neuenburg", en: "Neuchatel" },
  GE: { de: "Genf", en: "Geneva" },
  JU: { de: "Jura", en: "Jura" },
};

function getCantonName(code: string): string {
  return CANTON_NAMES[code]?.en || code;
}

/**
 * Role badge variant helper
 */
function getRoleBadgeVariant(
  role: string
): "default" | "secondary" | "destructive" | "outline" {
  switch (role) {
    case "super_admin":
      return "destructive";
    case "canton_admin":
      return "default";
    case "admin":
      return "default";
    case "accountant":
      return "secondary";
    default:
      return "outline";
  }
}

/**
 * Format role for display
 */
function formatRole(role: string): string {
  return role
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default function Admin() {
  const { user } = useAuth();

  // Check admin access (admin, canton_admin, or super_admin)
  if (
    !user?.role ||
    !ADMIN_ROLES.includes(user.role as (typeof ADMIN_ROLES)[number])
  ) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Access denied. This page is only available to platform
              administrators.
            </AlertDescription>
          </Alert>
        </div>
      </DashboardLayout>
    );
  }

  // Canton admins (not super_admin) see the canton-scoped view
  if (user.role === "canton_admin") {
    return (
      <DashboardLayout>
        <CantonAdminDashboard />
      </DashboardLayout>
    );
  }

  // Super admins and regular admins see the full admin view
  return (
    <DashboardLayout>
      <AdminContent />
    </DashboardLayout>
  );
}

/**
 * Canton Admin Dashboard
 * Scoped view for canton_admin users showing only their assigned cantons.
 */
function CantonAdminDashboard() {
  const [selectedCanton, setSelectedCanton] = useState<string | "all">("all");
  const [companyPage, setCompanyPage] = useState(1);
  const [companyDetailsDialog, setCompanyDetailsDialog] = useState<{
    id: number;
    name: string;
    uid: string | null;
    cantonCode: string | null;
    legalForm: string | null;
    createdAt: Date;
  } | null>(null);

  const utils = trpc.useUtils();

  // Fetch canton stats for assigned cantons
  const cantonStats = trpc.admin.getCantonStats.useQuery();

  // Fetch companies with pagination and optional canton filter
  const companiesList = trpc.admin.listCantonCompanies.useQuery({
    cantonCode: selectedCanton === "all" ? undefined : selectedCanton,
    page: companyPage,
    limit: 15,
  });

  const handleRefresh = () => {
    utils.admin.getCantonStats.invalidate();
    utils.admin.listCantonCompanies.invalidate();
    toast.success("Data refreshed");
  };

  // Get assigned canton codes from stats (filter out any null values)
  const assignedCantons =
    cantonStats.data?.stats
      ?.map(s => s.cantonCode)
      .filter((c): c is string => c !== null) || [];
  const hasMultipleCantons = assignedCantons.length > 1;
  const isAllCantons = cantonStats.data?.assignedCantons === "all";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <MapPin className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">
              Canton Administration
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              {isAllCantons
                ? "Managing all cantons"
                : `Managing ${assignedCantons.length} assigned canton${assignedCantons.length !== 1 ? "s" : ""}`}
            </p>
          </div>
        </div>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Canton Stats Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Shield className="h-5 w-5 text-muted-foreground" />
          Your Assigned Cantons
        </h2>

        {cantonStats.isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[1, 2, 3].map(i => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <Skeleton className="h-5 w-24" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-16 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : cantonStats.data?.stats.length === 0 ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No canton assignments found. Please contact a super admin to
              assign cantons to your account.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {cantonStats.data?.stats.map(stat => (
              <Card
                key={stat.cantonCode}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedCanton === stat.cantonCode
                    ? "ring-2 ring-primary"
                    : ""
                }`}
                onClick={() => {
                  setSelectedCanton(prev =>
                    prev === stat.cantonCode
                      ? "all"
                      : (stat.cantonCode ?? "all")
                  );
                  setCompanyPage(1);
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-base font-medium">
                    {getCantonName(stat.cantonCode ?? "")}
                  </CardTitle>
                  <Badge variant="outline" className="font-mono">
                    {stat.cantonCode}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-4">
                    <div>
                      <div className="text-2xl font-bold">
                        {stat.companyCount}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {stat.companyCount === 1 ? "company" : "companies"}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-muted-foreground">
                        {stat.userCount}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {stat.userCount === 1 ? "user" : "users"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Companies List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Companies
                {selectedCanton !== "all" && (
                  <Badge variant="secondary" className="ml-2">
                    {getCantonName(selectedCanton)}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {selectedCanton === "all"
                  ? "Companies across all your assigned cantons"
                  : `Companies in ${getCantonName(selectedCanton)}`}
              </CardDescription>
            </div>
            {(hasMultipleCantons || isAllCantons) && (
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select
                  value={selectedCanton}
                  onValueChange={value => {
                    setSelectedCanton(value as string);
                    setCompanyPage(1);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by canton" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All cantons</SelectItem>
                    {assignedCantons.map(code => (
                      <SelectItem key={code} value={code}>
                        {getCantonName(code)} ({code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company Name</TableHead>
                  <TableHead className="hidden sm:table-cell">UID</TableHead>
                  <TableHead>Canton</TableHead>
                  <TableHead className="hidden md:table-cell">
                    Legal Form
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Created
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companiesList.isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-12" />
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Skeleton className="h-4 w-16" />
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-8 w-16 ml-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : companiesList.data?.companies.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {selectedCanton === "all"
                        ? "No companies found in your assigned cantons"
                        : `No companies found in ${getCantonName(selectedCanton)}`}
                    </TableCell>
                  </TableRow>
                ) : (
                  companiesList.data?.companies.map(company => (
                    <TableRow key={company.id}>
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {company.name}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell font-mono text-xs text-muted-foreground">
                        {company.uid || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {company.cantonCode || "-"}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {company.legalForm || "-"}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-muted-foreground">
                        {new Date(company.createdAt).toLocaleDateString(
                          "de-CH"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCompanyDetailsDialog(company)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">View</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {companiesList.data &&
            companiesList.data.pagination.totalPages > 1 && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {(companyPage - 1) * 15 + 1} to{" "}
                  {Math.min(
                    companyPage * 15,
                    companiesList.data.pagination.total
                  )}{" "}
                  of {companiesList.data.pagination.total} companies
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCompanyPage(p => Math.max(1, p - 1))}
                    disabled={companyPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="flex items-center px-3 text-sm text-muted-foreground">
                    {companyPage} / {companiesList.data.pagination.totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCompanyPage(p =>
                        Math.min(
                          companiesList.data!.pagination.totalPages,
                          p + 1
                        )
                      )
                    }
                    disabled={
                      companyPage >= companiesList.data.pagination.totalPages
                    }
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick Actions</CardTitle>
          <CardDescription>Common canton administration tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" disabled>
              <Users className="h-4 w-4 mr-2" />
              View Users (Coming Soon)
            </Button>
            <Button variant="outline" size="sm" disabled>
              <Activity className="h-4 w-4 mr-2" />
              Activity Log (Coming Soon)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Company Details Dialog */}
      <Dialog
        open={!!companyDetailsDialog}
        onOpenChange={() => setCompanyDetailsDialog(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Company Details</DialogTitle>
            <DialogDescription>Viewing company information</DialogDescription>
          </DialogHeader>
          {companyDetailsDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Company Name
                  </Label>
                  <p className="font-medium">{companyDetailsDialog.name}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Canton
                  </Label>
                  <p className="font-medium">
                    {companyDetailsDialog.cantonCode
                      ? `${getCantonName(companyDetailsDialog.cantonCode)} (${companyDetailsDialog.cantonCode})`
                      : "-"}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">UID</Label>
                  <p className="font-mono text-sm">
                    {companyDetailsDialog.uid || "-"}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Legal Form
                  </Label>
                  <p className="font-medium">
                    {companyDetailsDialog.legalForm || "-"}
                  </p>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-muted-foreground">
                    Created
                  </Label>
                  <p className="font-medium">
                    {new Date(
                      companyDetailsDialog.createdAt
                    ).toLocaleDateString("de-CH", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCompanyDetailsDialog(null)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Assign Canton Admin Dialog
 * Allows super_admins to assign users as canton administrators with specific permissions.
 */
function AssignCantonAdminDialog({
  userId,
  userName,
  open,
  onOpenChange,
  currentAssignments,
}: {
  userId: number;
  userName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentAssignments: CantonAssignment[];
}) {
  const utils = trpc.useUtils();
  const [selectedCanton, setSelectedCanton] = useState<string>("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [permissions, setPermissions] = useState<CantonPermissions>({
    canManageUsers: true,
    canManageCompanies: true,
    canManageTaxConfig: false,
    canViewAnalytics: true,
    canManageCantonAdmins: false,
  });

  // Fetch cantons list
  const { data: cantons, isLoading: cantonsLoading } =
    trpc.admin.listCantons.useQuery(undefined, { enabled: open });

  // Assign canton admin mutation
  const assignMutation = trpc.admin.assignCantonAdmin.useMutation({
    onSuccess: () => {
      toast.success(`Canton assignment added for ${userName}`);
      utils.admin.getUserDetails.invalidate({ userId });
      utils.admin.listUsers.invalidate();
      resetForm();
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  // Revoke canton admin mutation
  const revokeMutation = trpc.admin.revokeCantonAdmin.useMutation({
    onSuccess: () => {
      toast.success("Canton assignment revoked");
      utils.admin.getUserDetails.invalidate({ userId });
      utils.admin.listUsers.invalidate();
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  const resetForm = () => {
    setSelectedCanton("");
    setIsPrimary(false);
    setPermissions({
      canManageUsers: true,
      canManageCompanies: true,
      canManageTaxConfig: false,
      canViewAnalytics: true,
      canManageCantonAdmins: false,
    });
  };

  const handleAssign = () => {
    if (!selectedCanton) {
      toast.error("Please select a canton");
      return;
    }

    assignMutation.mutate({
      userId,
      cantonCode: selectedCanton,
      isPrimary,
      permissions,
    });
  };

  const handleRevoke = (cantonCode: string) => {
    revokeMutation.mutate({ userId, cantonCode });
  };

  // Filter out already assigned cantons from selection
  const assignedCantonCodes = currentAssignments.map(a => a.cantonCode);
  const availableCantons = cantons?.filter(
    c => !assignedCantonCodes.includes(c.code)
  );

  const togglePermission = (key: keyof CantonPermissions) => {
    setPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Canton Admin Assignment
          </DialogTitle>
          <DialogDescription>
            Manage canton administrator assignments for{" "}
            <span className="font-semibold">{userName}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Current Assignments */}
        {currentAssignments.length > 0 && (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Current Assignments</Label>
            <div className="space-y-2">
              {currentAssignments.map(assignment => (
                <div
                  key={assignment.cantonCode}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{assignment.cantonCode}</Badge>
                    {assignment.isPrimary && (
                      <Badge variant="default" className="text-xs">
                        Primary
                      </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                      {cantons?.find(c => c.code === assignment.cantonCode)
                        ?.nameDe || assignment.cantonCode}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevoke(assignment.cantonCode)}
                    disabled={revokeMutation.isPending}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    {revokeMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add New Assignment */}
        <div className="space-y-4 pt-4 border-t">
          <Label className="text-sm font-medium">Add New Assignment</Label>

          {/* Canton Selection */}
          <div className="space-y-2">
            <Label htmlFor="canton-select">Canton</Label>
            <Select
              value={selectedCanton}
              onValueChange={setSelectedCanton}
              disabled={cantonsLoading}
            >
              <SelectTrigger id="canton-select">
                <SelectValue
                  placeholder={
                    cantonsLoading ? "Loading cantons..." : "Select a canton"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {availableCantons?.length === 0 ? (
                  <SelectItem value="none" disabled>
                    All cantons already assigned
                  </SelectItem>
                ) : (
                  availableCantons?.map(canton => (
                    <SelectItem key={canton.code} value={canton.code}>
                      {canton.code} - {canton.nameDe}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Primary Admin Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-primary"
              checked={isPrimary}
              onCheckedChange={checked => setIsPrimary(checked === true)}
            />
            <Label
              htmlFor="is-primary"
              className="text-sm font-normal cursor-pointer"
            >
              Primary Admin (can manage other canton admins)
            </Label>
          </div>

          {/* Permission Toggles */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Permissions</Label>
            <div className="space-y-3 pl-1">
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="perm-users"
                  className="text-sm font-normal cursor-pointer"
                >
                  Can Manage Users
                </Label>
                <Switch
                  id="perm-users"
                  checked={permissions.canManageUsers}
                  onCheckedChange={() => togglePermission("canManageUsers")}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="perm-companies"
                  className="text-sm font-normal cursor-pointer"
                >
                  Can Manage Companies
                </Label>
                <Switch
                  id="perm-companies"
                  checked={permissions.canManageCompanies}
                  onCheckedChange={() => togglePermission("canManageCompanies")}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="perm-tax"
                  className="text-sm font-normal cursor-pointer"
                >
                  Can Manage Tax Config
                </Label>
                <Switch
                  id="perm-tax"
                  checked={permissions.canManageTaxConfig}
                  onCheckedChange={() => togglePermission("canManageTaxConfig")}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="perm-analytics"
                  className="text-sm font-normal cursor-pointer"
                >
                  Can View Analytics
                </Label>
                <Switch
                  id="perm-analytics"
                  checked={permissions.canViewAnalytics}
                  onCheckedChange={() => togglePermission("canViewAnalytics")}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="perm-admins"
                  className="text-sm font-normal cursor-pointer"
                >
                  Can Manage Canton Admins
                </Label>
                <Switch
                  id="perm-admins"
                  checked={permissions.canManageCantonAdmins}
                  onCheckedChange={() =>
                    togglePermission("canManageCantonAdmins")
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedCanton || assignMutation.isPending}
          >
            {assignMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              "Assign Canton"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Loading skeleton for user details
 */
function UserDetailsSkeleton() {
  return (
    <div className="space-y-6 py-4">
      <div className="space-y-4">
        <Skeleton className="h-4 w-20" />
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-16" />
            </div>
          </div>
          <Separator />
          <div className="space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-44" />
          </div>
        </div>
      </div>
      <Separator />
      <div className="space-y-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-16 w-full rounded-lg" />
      </div>
      <Separator />
      <div className="space-y-3">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-12 w-full rounded-lg" />
      </div>
    </div>
  );
}

/**
 * User Details Sheet
 * Shows detailed information about a user including canton assignments.
 */
function UserDetailsSheet({
  userId,
  open,
  onOpenChange,
  onAssignCantonClick,
  onRoleChange,
}: {
  userId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssignCantonClick: () => void;
  onRoleChange: (
    userId: number,
    userName: string,
    currentRole: string,
    newRole: "user" | "admin"
  ) => void;
}) {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const utils = trpc.useUtils();

  const {
    data: userDetails,
    isLoading,
    error,
  } = trpc.admin.getUserDetails.useQuery(
    { userId: userId! },
    { enabled: open && userId !== null }
  );

  const promoteMutation = trpc.admin.promoteToSuperAdmin.useMutation({
    onSuccess: () => {
      toast.success("User promoted to Super Admin");
      utils.admin.getUserDetails.invalidate({ userId: userId! });
      utils.admin.listUsers.invalidate();
    },
    onError: err => {
      toast.error(err.message);
    },
  });

  const canPromoteToSuperAdmin =
    isSuperAdmin &&
    userDetails &&
    ["admin", "canton_admin"].includes(userDetails.role) &&
    currentUser?.id !== userId;

  if (!userId) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            User Details
          </SheetTitle>
          <SheetDescription>
            View complete user profile and access information
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <UserDetailsSkeleton />
        ) : error ? (
          <div className="py-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          </div>
        ) : userDetails ? (
          <div className="py-4 space-y-6">
            {/* User Profile Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Profile
              </h3>
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {userDetails.name || "No name set"}
                      </p>
                      <Badge variant={getRoleBadgeVariant(userDetails.role)}>
                        {formatRole(userDetails.role)}
                      </Badge>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="grid gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Email:</span>
                    <span className="font-medium">
                      {userDetails.email || "Not set"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Joined:</span>
                    <span className="font-medium">
                      {userDetails.createdAt
                        ? new Date(userDetails.createdAt).toLocaleDateString(
                            "en-CH",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )
                        : "Unknown"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Last Active:</span>
                    <span className="font-medium">
                      {userDetails.lastSignedIn
                        ? new Date(userDetails.lastSignedIn).toLocaleDateString(
                            "en-CH",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )
                        : "Never"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Owned Companies Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Owned Companies
                <Badge variant="outline" className="ml-auto">
                  {userDetails.ownedCompanies.length}
                </Badge>
              </h3>
              {userDetails.ownedCompanies.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  No companies owned
                </p>
              ) : (
                <div className="space-y-2">
                  {userDetails.ownedCompanies.map(company => (
                    <div
                      key={company.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm">{company.name}</p>
                          {company.uid && (
                            <p className="text-xs text-muted-foreground">
                              UID: {company.uid}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {company.cantonCode && (
                          <Badge variant="outline" className="text-xs">
                            {company.cantonCode}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => {
                            window.location.href = `/company/${company.id}`;
                          }}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Accountant Access Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Accountant Access
                <Badge variant="outline" className="ml-auto">
                  {userDetails.accountantAccess.length}
                </Badge>
              </h3>
              {userDetails.accountantAccess.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  No accountant access granted
                </p>
              ) : (
                <div className="space-y-2">
                  {userDetails.accountantAccess.map(access => (
                    <div
                      key={access.companyId}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Briefcase className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm">
                            {access.companyName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Access: {access.accessLevel}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => {
                          window.location.href = `/company/${access.companyId}`;
                        }}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Canton Assignments Section (for canton_admin) */}
            {userDetails.cantonAssignments.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Canton Assignments
                      <Badge variant="outline" className="ml-auto">
                        {userDetails.cantonAssignments.length}
                      </Badge>
                    </h3>
                    {isSuperAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onAssignCantonClick}
                      >
                        Manage
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {userDetails.cantonAssignments.map(assignment => (
                      <div
                        key={assignment.cantonCode}
                        className="p-3 bg-muted/50 rounded-lg space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant="default">
                              {assignment.cantonCode}
                            </Badge>
                            {assignment.isPrimary && (
                              <Badge variant="secondary" className="text-xs">
                                Primary
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            Since{" "}
                            {assignment.assignedAt
                              ? new Date(
                                  assignment.assignedAt
                                ).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>
                        {assignment.permissions && (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {(assignment.permissions as CantonPermissions)
                              .canManageUsers && (
                              <Badge variant="outline" className="text-xs">
                                Manage Users
                              </Badge>
                            )}
                            {(assignment.permissions as CantonPermissions)
                              .canManageCompanies && (
                              <Badge variant="outline" className="text-xs">
                                Manage Companies
                              </Badge>
                            )}
                            {(assignment.permissions as CantonPermissions)
                              .canManageTaxConfig && (
                              <Badge variant="outline" className="text-xs">
                                Tax Config
                              </Badge>
                            )}
                            {(assignment.permissions as CantonPermissions)
                              .canViewAnalytics && (
                              <Badge variant="outline" className="text-xs">
                                Analytics
                              </Badge>
                            )}
                            {(assignment.permissions as CantonPermissions)
                              .canManageCantonAdmins && (
                              <Badge variant="outline" className="text-xs">
                                Manage Admins
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            User not found
          </div>
        )}

        {userDetails && (
          <SheetFooter className="flex-col sm:flex-row gap-2 border-t pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                onRoleChange(
                  userDetails.id,
                  userDetails.name ||
                    userDetails.email ||
                    `User #${userDetails.id}`,
                  userDetails.role,
                  userDetails.role === "admin" ? "user" : "admin"
                );
                onOpenChange(false);
              }}
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              {userDetails.role === "admin"
                ? "Demote to User"
                : "Promote to Admin"}
            </Button>
            {canPromoteToSuperAdmin && (
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => {
                  promoteMutation.mutate({ userId: userDetails.id });
                }}
                disabled={promoteMutation.isPending}
              >
                <Crown className="h-4 w-4 mr-2" />
                {promoteMutation.isPending
                  ? "Promoting..."
                  : "Promote to Super Admin"}
              </Button>
            )}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * Feature Flags Management Section - Super Admin / Canton Admin only
 * Full-featured flag management with overrides and rollout percentages
 */
function FeatureFlagsSection() {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [expandedFlag, setExpandedFlag] = useState<string | null>(null);
  const [newFlagOpen, setNewFlagOpen] = useState(false);
  const [newFlagForm, setNewFlagForm] = useState({
    name: "",
    description: "",
    defaultValue: false,
    rolloutPercentage: 100,
  });
  const [overrideForm, setOverrideForm] = useState<{
    flagName: string;
    scope: "user" | "company";
    scopeId: string;
    enabled: boolean;
  } | null>(null);

  const isSuperAdmin = user?.role === "super_admin";
  const isCantonAdmin =
    user?.role === "canton_admin" || user?.role === "super_admin";

  const { data: flags, isLoading } = trpc.featureFlags.list.useQuery(
    undefined,
    { enabled: isCantonAdmin }
  );

  const updateMutation = trpc.featureFlags.update.useMutation({
    onSuccess: () => {
      utils.featureFlags.list.invalidate();
      toast.success("Flag updated");
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const createMutation = trpc.featureFlags.create.useMutation({
    onSuccess: () => {
      utils.featureFlags.list.invalidate();
      setNewFlagOpen(false);
      setNewFlagForm({
        name: "",
        description: "",
        defaultValue: false,
        rolloutPercentage: 100,
      });
      toast.success("Flag created");
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const setOverrideMutation = trpc.featureFlags.setOverride.useMutation({
    onSuccess: () => {
      utils.featureFlags.list.invalidate();
      if (expandedFlag) {
        utils.featureFlags.listOverrides.invalidate({
          flagName: expandedFlag,
        });
      }
      setOverrideForm(null);
      toast.success("Override saved");
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  const removeOverrideMutation = trpc.featureFlags.removeOverride.useMutation({
    onSuccess: () => {
      utils.featureFlags.list.invalidate();
      if (expandedFlag) {
        utils.featureFlags.listOverrides.invalidate({
          flagName: expandedFlag,
        });
      }
      toast.success("Override removed");
    },
    onError: (error: { message: string }) => {
      toast.error(error.message);
    },
  });

  if (!isCantonAdmin) return null;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Flag className="h-5 w-5" />
                Feature Flags
              </CardTitle>
              <CardDescription>
                Manage feature rollouts with per-user and per-company overrides
              </CardDescription>
            </div>
            {isSuperAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNewFlagOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                New Flag
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-4 text-muted-foreground">
              Loading flags...
            </div>
          ) : !flags || flags.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No feature flags configured. Create one to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {flags.map(flag => (
                <FeatureFlagRow
                  key={flag.name}
                  flag={flag}
                  isExpanded={expandedFlag === flag.name}
                  isSuperAdmin={isSuperAdmin}
                  onToggle={expanded =>
                    setExpandedFlag(expanded ? flag.name : null)
                  }
                  onUpdate={(defaultValue, rolloutPercentage) =>
                    updateMutation.mutate({
                      name: flag.name,
                      defaultValue,
                      rolloutPercentage,
                    })
                  }
                  onAddOverride={() =>
                    setOverrideForm({
                      flagName: flag.name,
                      scope: "user",
                      scopeId: "",
                      enabled: true,
                    })
                  }
                  onRemoveOverride={id => removeOverrideMutation.mutate({ id })}
                  isPending={
                    updateMutation.isPending || removeOverrideMutation.isPending
                  }
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Flag Dialog */}
      <Dialog open={newFlagOpen} onOpenChange={setNewFlagOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Feature Flag</DialogTitle>
            <DialogDescription>
              Define a new feature flag with an optional rollout percentage.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="ff-name">Name</Label>
              <Input
                id="ff-name"
                placeholder="my_new_feature"
                value={newFlagForm.name}
                onChange={e =>
                  setNewFlagForm(f => ({ ...f, name: e.target.value }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Lowercase letters, numbers, and underscores only
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ff-desc">Description</Label>
              <Input
                id="ff-desc"
                placeholder="What this flag controls..."
                value={newFlagForm.description}
                onChange={e =>
                  setNewFlagForm(f => ({ ...f, description: e.target.value }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="ff-default">Default Enabled</Label>
              <Switch
                id="ff-default"
                checked={newFlagForm.defaultValue}
                onCheckedChange={checked =>
                  setNewFlagForm(f => ({ ...f, defaultValue: checked }))
                }
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Rollout Percentage</Label>
                <span className="text-sm font-mono text-muted-foreground">
                  {newFlagForm.rolloutPercentage}%
                </span>
              </div>
              <Slider
                value={[newFlagForm.rolloutPercentage]}
                onValueChange={([val]) =>
                  setNewFlagForm(f => ({ ...f, rolloutPercentage: val }))
                }
                min={0}
                max={100}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFlagOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate(newFlagForm)}
              disabled={
                !newFlagForm.name ||
                !/^[a-z][a-z0-9_]*$/.test(newFlagForm.name) ||
                createMutation.isPending
              }
            >
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : null}
              Create Flag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override Dialog */}
      <Dialog
        open={overrideForm !== null}
        onOpenChange={open => !open && setOverrideForm(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Override</DialogTitle>
            <DialogDescription>
              Override flag &quot;{overrideForm?.flagName}&quot; for a specific
              user or company.
            </DialogDescription>
          </DialogHeader>
          {overrideForm && (
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label>Scope</Label>
                <Select
                  value={overrideForm.scope}
                  onValueChange={(val: "user" | "company") =>
                    setOverrideForm(f =>
                      f ? { ...f, scope: val, scopeId: "" } : null
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>
                  {overrideForm.scope === "user" ? "User ID" : "Company ID"}
                </Label>
                <Input
                  placeholder={
                    overrideForm.scope === "user"
                      ? "Enter user ID..."
                      : "Enter company ID..."
                  }
                  value={overrideForm.scopeId}
                  onChange={e =>
                    setOverrideForm(f =>
                      f ? { ...f, scopeId: e.target.value } : null
                    )
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Enabled</Label>
                <Switch
                  checked={overrideForm.enabled}
                  onCheckedChange={checked =>
                    setOverrideForm(f =>
                      f ? { ...f, enabled: checked } : null
                    )
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideForm(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (overrideForm) {
                  setOverrideMutation.mutate(overrideForm);
                }
              }}
              disabled={!overrideForm?.scopeId || setOverrideMutation.isPending}
            >
              {setOverrideMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : null}
              Save Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Individual feature flag row with expand/collapse for overrides
 */
function FeatureFlagRow({
  flag,
  isExpanded,
  isSuperAdmin,
  onToggle,
  onUpdate,
  onAddOverride,
  onRemoveOverride,
  isPending,
}: {
  flag: {
    name: string;
    description: string | null;
    defaultValue: boolean;
    rolloutPercentage: number;
    overrideCount: number;
    updatedAt: Date;
  };
  isExpanded: boolean;
  isSuperAdmin: boolean;
  onToggle: (expanded: boolean) => void;
  onUpdate: (defaultValue?: boolean, rolloutPercentage?: number) => void;
  onAddOverride: () => void;
  onRemoveOverride: (id: number) => void;
  isPending: boolean;
}) {
  const [rollout, setRollout] = useState(flag.rolloutPercentage);

  const { data: overrides } = trpc.featureFlags.listOverrides.useQuery(
    { flagName: flag.name },
    { enabled: isExpanded && isSuperAdmin }
  );

  return (
    <div className="border rounded-lg">
      {/* Flag header row */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button
            className="text-left flex-1 min-w-0"
            onClick={() => onToggle(!isExpanded)}
          >
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-medium truncate">
                {flag.name}
              </span>
              {flag.overrideCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {flag.overrideCount} override
                  {flag.overrideCount !== 1 ? "s" : ""}
                </Badge>
              )}
            </div>
            {flag.description && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {flag.description}
              </p>
            )}
          </button>
        </div>
        <div className="flex items-center gap-3 ml-3 shrink-0">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Percent className="h-3 w-3" />
            <span className="font-mono">{flag.rolloutPercentage}%</span>
          </div>
          {isSuperAdmin && (
            <Switch
              checked={flag.defaultValue}
              onCheckedChange={checked => onUpdate(checked, undefined)}
              disabled={isPending}
            />
          )}
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="border-t px-3 py-3 space-y-3">
          {/* Rollout slider */}
          {isSuperAdmin && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Rollout Percentage</Label>
                <span className="text-xs font-mono text-muted-foreground">
                  {rollout}%
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Slider
                  value={[rollout]}
                  onValueChange={([val]) => setRollout(val)}
                  min={0}
                  max={100}
                  className="flex-1"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onUpdate(undefined, rollout)}
                  disabled={rollout === flag.rolloutPercentage || isPending}
                  className="text-xs"
                >
                  Apply
                </Button>
              </div>
            </div>
          )}

          {/* Overrides list */}
          {isSuperAdmin && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Overrides</Label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onAddOverride}
                  className="h-6 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              {overrides && overrides.length > 0 ? (
                <div className="space-y-1">
                  {overrides.map(override => (
                    <div
                      key={override.id}
                      className="flex items-center justify-between rounded px-2 py-1 bg-muted/50 text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs capitalize">
                          {override.scope}
                        </Badge>
                        <span className="font-mono">{override.scopeId}</span>
                        <Badge
                          variant={override.enabled ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {override.enabled ? "ON" : "OFF"}
                        </Badge>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onRemoveOverride(override.id)}
                        className="h-6 w-6 p-0"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No overrides configured
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * System Settings Section - Super Admin only
 * Allows toggling platform-wide feature flags
 */
function SystemSettingsSection() {
  const { user } = useAuth();
  const utils = trpc.useUtils();

  const { data: settings, isLoading } = trpc.admin.getSystemSettings.useQuery(
    undefined,
    { enabled: user?.role === "super_admin" }
  );

  const updateMutation = trpc.admin.updateSystemSetting.useMutation({
    onSuccess: () => {
      utils.admin.getSystemSettings.invalidate();
      toast.success("Setting updated");
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  // Only show to super_admin
  if (user?.role !== "super_admin") return null;

  const censusSetting = settings?.find(
    s => s.key === "census_first_pipeline_enabled"
  );
  const isEnabled =
    (censusSetting?.value as { enabled?: boolean })?.enabled ?? true;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          System Settings
        </CardTitle>
        <CardDescription>
          Platform-wide configuration (super-admin only)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="text-center py-4 text-muted-foreground">
            Loading settings...
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="census-first-toggle">Census-First Pipeline</Label>
              <p className="text-sm text-muted-foreground">
                Use 6-phase extraction (DETECT, CENSUS, STRATEGY, EXTRACT,
                ATTRIBUTE, PLACE)
              </p>
            </div>
            <Switch
              id="census-first-toggle"
              checked={isEnabled}
              onCheckedChange={checked => {
                updateMutation.mutate({
                  key: "census_first_pipeline_enabled",
                  value: { enabled: checked },
                });
              }}
              disabled={updateMutation.isPending}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Canton Management Section - Super Admin only
 * Allows viewing and editing canton configurations
 */
function CantonManagementSection() {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [editingCanton, setEditingCanton] = useState<Canton | null>(null);
  const [editForm, setEditForm] = useState<{
    status: CantonStatus;
    corporateTaxRate: string;
    enableOnboarding: boolean;
    enableAiAssistant: boolean;
    enableBankIntegrations: boolean;
    enableMultiCurrency: boolean;
  } | null>(null);

  const { data: cantons, isLoading } = trpc.admin.listCantons.useQuery(
    undefined,
    { enabled: user?.role === "super_admin" }
  );

  const updateMutation = trpc.admin.updateCanton.useMutation({
    onSuccess: () => {
      utils.admin.listCantons.invalidate();
      toast.success("Canton updated successfully");
      setEditingCanton(null);
      setEditForm(null);
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  // Only show to super_admin
  if (user?.role !== "super_admin") return null;

  const handleEditClick = (canton: Canton) => {
    setEditingCanton(canton);
    setEditForm({
      status: canton.status,
      corporateTaxRate: canton.taxConfig?.corporateTaxRate?.toString() || "",
      enableOnboarding: canton.featureFlags?.enableOnboarding ?? true,
      enableAiAssistant: canton.featureFlags?.enableAiAssistant ?? true,
      enableBankIntegrations:
        canton.featureFlags?.enableBankIntegrations ?? false,
      enableMultiCurrency: canton.featureFlags?.enableMultiCurrency ?? false,
    });
  };

  const handleSave = () => {
    if (!editingCanton || !editForm) return;

    const taxRate = parseFloat(editForm.corporateTaxRate);

    updateMutation.mutate({
      code: editingCanton.code,
      status: editForm.status,
      taxConfig: {
        ...editingCanton.taxConfig,
        corporateTaxRate: isNaN(taxRate) ? undefined : taxRate,
      },
      featureFlags: {
        enableOnboarding: editForm.enableOnboarding,
        enableAiAssistant: editForm.enableAiAssistant,
        enableBankIntegrations: editForm.enableBankIntegrations,
        enableMultiCurrency: editForm.enableMultiCurrency,
      },
    });
  };

  const getStatusBadgeVariant = (
    status: CantonStatus
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "active":
        return "default";
      case "beta":
        return "outline";
      case "inactive":
        return "secondary";
      default:
        return "secondary";
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Canton Management
          </CardTitle>
          <CardDescription>
            Manage Swiss cantons, tax configurations, and feature flags
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading cantons...
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Admins</TableHead>
                  <TableHead className="text-right">Companies</TableHead>
                  <TableHead className="text-right">Corp. Tax</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cantons?.map(canton => (
                  <TableRow key={canton.code}>
                    <TableCell className="font-mono font-medium">
                      {canton.code}
                    </TableCell>
                    <TableCell>{canton.nameDe}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(canton.status)}>
                        {canton.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {canton.adminCount}
                    </TableCell>
                    <TableCell className="text-right">
                      {canton.companyCount}
                    </TableCell>
                    <TableCell className="text-right">
                      {canton.taxConfig?.corporateTaxRate
                        ? `${canton.taxConfig.corporateTaxRate}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditClick(canton)}
                      >
                        <Pencil className="h-4 w-4" />
                        <span className="sr-only">Edit {canton.nameDe}</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Canton Dialog */}
      <Dialog
        open={!!editingCanton}
        onOpenChange={open => {
          if (!open) {
            setEditingCanton(null);
            setEditForm(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Edit Canton: {editingCanton?.nameDe} ({editingCanton?.code})
            </DialogTitle>
            <DialogDescription>
              Update status, tax configuration, and feature flags for this
              canton.
            </DialogDescription>
          </DialogHeader>

          {editForm && (
            <div className="space-y-6 py-4">
              {/* Status */}
              <div className="space-y-2">
                <Label htmlFor="canton-status">Status</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(value: CantonStatus) =>
                    setEditForm({ ...editForm, status: value })
                  }
                >
                  <SelectTrigger id="canton-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="beta">Beta</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Corporate Tax Rate */}
              <div className="space-y-2">
                <Label htmlFor="corp-tax-rate">Corporate Tax Rate (%)</Label>
                <Input
                  id="corp-tax-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="e.g., 14.5"
                  value={editForm.corporateTaxRate}
                  onChange={e =>
                    setEditForm({
                      ...editForm,
                      corporateTaxRate: e.target.value,
                    })
                  }
                />
              </div>

              {/* Feature Flags */}
              <div className="space-y-4">
                <Label className="text-base">Feature Flags</Label>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="enable-onboarding" className="font-normal">
                      Enable Onboarding
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Allow new company registration in this canton
                    </p>
                  </div>
                  <Switch
                    id="enable-onboarding"
                    checked={editForm.enableOnboarding}
                    onCheckedChange={checked =>
                      setEditForm({ ...editForm, enableOnboarding: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="enable-ai" className="font-normal">
                      Enable AI Assistant
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Enable AI-powered features for companies
                    </p>
                  </div>
                  <Switch
                    id="enable-ai"
                    checked={editForm.enableAiAssistant}
                    onCheckedChange={checked =>
                      setEditForm({ ...editForm, enableAiAssistant: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="enable-bank" className="font-normal">
                      Enable Bank Integrations
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Allow direct bank connections
                    </p>
                  </div>
                  <Switch
                    id="enable-bank"
                    checked={editForm.enableBankIntegrations}
                    onCheckedChange={checked =>
                      setEditForm({
                        ...editForm,
                        enableBankIntegrations: checked,
                      })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label
                      htmlFor="enable-multi-currency"
                      className="font-normal"
                    >
                      Enable Multi-Currency
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Support multiple currencies beyond CHF
                    </p>
                  </div>
                  <Switch
                    id="enable-multi-currency"
                    checked={editForm.enableMultiCurrency}
                    onCheckedChange={checked =>
                      setEditForm({ ...editForm, enableMultiCurrency: checked })
                    }
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditingCanton(null);
                setEditForm(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AdminContent() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";

  const [userPage, setUserPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [roleChangeDialog, setRoleChangeDialog] = useState<{
    userId: number;
    userName: string;
    currentRole: string;
    newRole: "user" | "admin";
  } | null>(null);

  // User details sheet state
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);

  // Canton admin dialog state
  const [cantonDialogUser, setCantonDialogUser] = useState<{
    userId: number;
    userName: string;
    assignments: CantonAssignment[];
  } | null>(null);

  const utils = trpc.useUtils();

  // Queries
  const systemStats = trpc.admin.getSystemStats.useQuery();
  const aiUsageStats = trpc.admin.getAiUsageStats.useQuery({ days: 30 });
  const aiByCompany = trpc.admin.getAiUsageByCompany.useQuery({
    days: 30,
    limit: 5,
  });
  const usersList = trpc.admin.listUsers.useQuery({
    page: userPage,
    limit: 10,
    role: roleFilter === "all" ? undefined : roleFilter,
  });

  // Get user details for canton dialog (when opening from table)
  const { data: selectedUserDetails } = trpc.admin.getUserDetails.useQuery(
    { userId: cantonDialogUser?.userId ?? 0 },
    { enabled: cantonDialogUser !== null && cantonDialogUser.userId > 0 }
  );

  // Mutations
  const updateRole = trpc.admin.updateUserRole.useMutation({
    onSuccess: data => {
      toast.success(
        `Role updated from ${data.previousRole} to ${data.newRole}`
      );
      utils.admin.listUsers.invalidate();
      utils.admin.getUserDetails.invalidate();
      setRoleChangeDialog(null);
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  const handleRefresh = () => {
    utils.admin.getSystemStats.invalidate();
    utils.admin.getAiUsageStats.invalidate();
    utils.admin.getAiUsageByCompany.invalidate();
    utils.admin.listUsers.invalidate();
    utils.admin.listCantons.invalidate();
    toast.success("Data refreshed");
  };

  const formatTokens = (tokens: number): string => {
    if (tokens >= 1_000_000) {
      return `${(tokens / 1_000_000).toFixed(1)}M`;
    }
    if (tokens >= 1_000) {
      return `${(tokens / 1_000).toFixed(1)}K`;
    }
    return tokens.toString();
  };

  const formatCurrency = (cents: number): string => {
    return `CHF ${(cents / 100).toFixed(2)}`;
  };

  // Handle opening canton dialog from user details sheet
  const handleOpenCantonDialogFromSheet = () => {
    if (selectedUserId && selectedUserDetails) {
      setCantonDialogUser({
        userId: selectedUserId,
        userName:
          selectedUserDetails.name ||
          selectedUserDetails.email ||
          `User #${selectedUserId}`,
        assignments:
          selectedUserDetails.cantonAssignments as CantonAssignment[],
      });
    }
  };

  // Handle role change from sheet
  const handleRoleChangeFromSheet = (
    userId: number,
    userName: string,
    currentRole: string,
    newRole: "user" | "admin"
  ) => {
    setRoleChangeDialog({
      userId,
      userName,
      currentRole,
      newRole,
    });
  };

  // Handle opening canton dialog directly from table row
  const handleOpenCantonDialogFromTable = (user: {
    id: number;
    name: string | null;
    email: string | null;
  }) => {
    // We'll fetch the user details when the dialog opens
    setCantonDialogUser({
      userId: user.id,
      userName: user.name || user.email || `User #${user.id}`,
      assignments: [], // Will be populated when query runs
    });
  };

  // Handle opening user details sheet
  const handleViewUser = (userId: number) => {
    setSelectedUserId(userId);
    setUserDetailsOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground">
              Platform administration and monitoring
            </p>
          </div>
        </div>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* System Settings - Super Admin Only */}
      <SystemSettingsSection />

      {/* Feature Flags Management - Super Admin / Canton Admin */}
      <FeatureFlagsSection />

      {/* Canton Management - Super Admin Only */}
      <CantonManagementSection />

      {/* System Overview */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {systemStats.isLoading
                ? "..."
                : systemStats.data?.totalUsers.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {systemStats.data?.activeUsersLast7Days} active last 7 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Companies</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {systemStats.isLoading
                ? "..."
                : systemStats.data?.totalCompanies.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Registered companies
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Transactions</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {systemStats.isLoading
                ? "..."
                : systemStats.data?.totalTransactions.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Total extracted entities
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">AI Cost (30d)</CardTitle>
            <Coins className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {aiUsageStats.isLoading
                ? "..."
                : formatCurrency(aiUsageStats.data?.totals.totalCostCents || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatTokens(aiUsageStats.data?.totals.totalTokens || 0)} tokens
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Funnel */}
      <FunnelSection />

      {/* User Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                User Management
              </CardTitle>
              <CardDescription>Manage platform users and roles</CardDescription>
            </div>
            <Select
              value={roleFilter}
              onValueChange={value => {
                setRoleFilter(value as RoleFilter);
                setUserPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="accountant">Accountant</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="canton_admin">Canton Admin</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersList.isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : usersList.data?.users.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                usersList.data?.users.map(user => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.name || "-"}
                    </TableCell>
                    <TableCell>{user.email || "-"}</TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(user.role)}>
                        {formatRole(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.lastSignedIn
                        ? new Date(user.lastSignedIn).toLocaleDateString()
                        : "Never"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewUser(user.id)}
                          title="View user details"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        {isSuperAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleOpenCantonDialogFromTable(user)
                            }
                            title="Manage Canton Assignments"
                          >
                            <MapPin className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setRoleChangeDialog({
                              userId: user.id,
                              userName:
                                user.name || user.email || `User #${user.id}`,
                              currentRole: user.role,
                              newRole: user.role === "admin" ? "user" : "admin",
                            })
                          }
                        >
                          {user.role === "admin" ? "Demote" : "Promote"}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {usersList.data && usersList.data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Page {usersList.data.pagination.page} of{" "}
                {usersList.data.pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUserPage(p => Math.max(1, p - 1))}
                  disabled={userPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setUserPage(p =>
                      Math.min(usersList.data!.pagination.totalPages, p + 1)
                    )
                  }
                  disabled={userPage >= usersList.data.pagination.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Usage Analytics */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cpu className="h-5 w-5" />
              AI Usage by Model (30d)
            </CardTitle>
            <CardDescription>Token usage and costs by model</CardDescription>
          </CardHeader>
          <CardContent>
            {aiUsageStats.isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading...
              </div>
            ) : aiUsageStats.data?.byModel.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No AI usage data
              </div>
            ) : (
              <div className="space-y-3">
                {aiUsageStats.data?.byModel.map(model => (
                  <div
                    key={model.model}
                    className="flex items-center justify-between p-2 bg-muted rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-sm">{model.model}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatTokens(model.totalTokens)} tokens |{" "}
                        {model.requestCount} requests
                      </p>
                    </div>
                    <Badge variant="outline">
                      {formatCurrency(model.totalCostCents)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Top Companies by AI Usage (30d)
            </CardTitle>
            <CardDescription>
              Companies with highest AI consumption
            </CardDescription>
          </CardHeader>
          <CardContent>
            {aiByCompany.isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading...
              </div>
            ) : aiByCompany.data?.companies.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No usage data
              </div>
            ) : (
              <div className="space-y-3">
                {aiByCompany.data?.companies.map((company, idx) => (
                  <div
                    key={company.companyId}
                    className="flex items-center justify-between p-2 bg-muted rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-muted-foreground">
                        #{idx + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm">
                          {company.companyName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatTokens(company.totalTokens)} tokens |{" "}
                          {company.requestCount} requests
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline">
                      {formatCurrency(company.totalCostCents)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Role Change Dialog */}
      <Dialog
        open={!!roleChangeDialog}
        onOpenChange={() => setRoleChangeDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to change the role for{" "}
              <span className="font-semibold">
                {roleChangeDialog?.userName}
              </span>{" "}
              from{" "}
              <Badge variant="secondary">{roleChangeDialog?.currentRole}</Badge>{" "}
              to{" "}
              <Badge
                variant={
                  roleChangeDialog?.newRole === "admin"
                    ? "default"
                    : "secondary"
                }
              >
                {roleChangeDialog?.newRole}
              </Badge>
              ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleChangeDialog(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (roleChangeDialog) {
                  updateRole.mutate({
                    userId: roleChangeDialog.userId,
                    role: roleChangeDialog.newRole,
                  });
                }
              }}
              disabled={updateRole.isPending}
            >
              {updateRole.isPending ? "Updating..." : "Confirm Change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Details Sheet */}
      <UserDetailsSheet
        userId={selectedUserId}
        open={userDetailsOpen}
        onOpenChange={open => {
          setUserDetailsOpen(open);
          if (!open) setSelectedUserId(null);
        }}
        onAssignCantonClick={handleOpenCantonDialogFromSheet}
        onRoleChange={handleRoleChangeFromSheet}
      />

      {/* Canton Admin Assignment Dialog */}
      {cantonDialogUser && (
        <AssignCantonAdminDialog
          userId={cantonDialogUser.userId}
          userName={cantonDialogUser.userName}
          open={true}
          onOpenChange={open => {
            if (!open) setCantonDialogUser(null);
          }}
          currentAssignments={
            (selectedUserDetails?.cantonAssignments as CantonAssignment[]) ??
            cantonDialogUser.assignments
          }
        />
      )}
    </div>
  );
}
